/**
 * AdvertiserPortalPage — advertiser-scoped conversions + reports dashboard.
 *
 * Sections:
 *   1. Controls bar — advertiser id + date range + campaign filter.
 *   2. 3 KPI tiles — total conversions, total revenue, total campaigns.
 *   3. Daily rollup table — UTC day bucketed count + revenue.
 *   4. Campaigns leaderboard — ordered by revenue desc (server-sorted).
 *   5. Recent conversions — paginated list from /conversions.
 *
 * Palette is CF-green only: green-600 / green-700 / green-50, white cards,
 * zinc-200 borders, zinc-900 text. No emerald, no sky, no gradient headlines.
 * Inline SVG icons only. All raw `fetch()` stays in api.ts.
 */
import { useMemo, useState } from "react";
import {
	useCampaignReport,
	useConversionsList,
	useDailyReport,
} from "./hooks";
import type {
	CampaignAggregateRow,
	ConversionStatus,
	ConversionView,
	DailyRollupRow,
} from "./api";

const STORAGE_KEY = "trackerity.advertiser_id";

function todayIso(): string {
	return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(n: number): string {
	const d = new Date();
	d.setDate(d.getDate() - n);
	return d.toISOString().slice(0, 10);
}

function isoToUnixSec(iso: string, endOfDay = false): number | undefined {
	if (!iso) return undefined;
	const suffix = endOfDay ? "T23:59:59Z" : "T00:00:00Z";
	const t = Date.parse(`${iso}${suffix}`);
	if (!Number.isFinite(t)) return undefined;
	return Math.floor(t / 1000);
}

function fmtInt(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return Math.round(n).toLocaleString();
}

function fmtCents(cents: number): string {
	if (!Number.isFinite(cents)) return "$0.00";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const dollars = Math.floor(abs / 100);
	const rem = abs % 100;
	return `${sign}$${dollars.toLocaleString()}.${rem.toString().padStart(2, "0")}`;
}

function fmtTs(sec: number): string {
	if (!Number.isFinite(sec)) return "—";
	const d = new Date(sec * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleString();
}

function readStoredAdvertiserId(): string {
	if (typeof window === "undefined" || !window.localStorage) return "";
	try {
		return window.localStorage.getItem(STORAGE_KEY) ?? "";
	} catch {
		return "";
	}
}

function writeStoredAdvertiserId(value: string): void {
	if (typeof window === "undefined" || !window.localStorage) return;
	try {
		window.localStorage.setItem(STORAGE_KEY, value);
	} catch {
		// ignore — storage quota / privacy mode
	}
}

const STATUS_CHIP: Record<ConversionStatus, string> = {
	pending: "bg-zinc-100 text-zinc-700 ring-1 ring-zinc-300",
	approved: "bg-green-50 text-green-700 ring-1 ring-green-600/20",
	rejected: "bg-rose-50 text-rose-700 ring-1 ring-rose-600/20",
	refunded: "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20",
};

function IconCalendar() {
	return (
		<svg
			aria-hidden
			width={14}
			height={14}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<rect x="3" y="4" width="18" height="18" rx="2" />
			<line x1="16" y1="2" x2="16" y2="6" />
			<line x1="8" y1="2" x2="8" y2="6" />
			<line x1="3" y1="10" x2="21" y2="10" />
		</svg>
	);
}

function IconTarget() {
	return (
		<svg
			aria-hidden
			width={16}
			height={16}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="12" cy="12" r="10" />
			<circle cx="12" cy="12" r="6" />
			<circle cx="12" cy="12" r="2" />
		</svg>
	);
}

function IconDollar() {
	return (
		<svg
			aria-hidden
			width={16}
			height={16}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<line x1="12" y1="1" x2="12" y2="23" />
			<path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
		</svg>
	);
}

function IconFlag() {
	return (
		<svg
			aria-hidden
			width={16}
			height={16}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
			<line x1="4" y1="22" x2="4" y2="15" />
		</svg>
	);
}

function KpiTile({
	label,
	value,
	icon,
	testId,
}: {
	label: string;
	value: string;
	icon: React.ReactNode;
	testId: string;
}) {
	return (
		<div
			data-testid={testId}
			className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm"
		>
			<div className="flex items-center justify-between">
				<div className="text-xs font-medium uppercase tracking-wider text-zinc-500">
					{label}
				</div>
				<div className="flex h-8 w-8 items-center justify-center rounded-md bg-green-50 text-green-700">
					{icon}
				</div>
			</div>
			<div className="mt-3 text-3xl font-semibold tabular-nums text-zinc-900">
				{value}
			</div>
		</div>
	);
}

function StatusChip({ status }: { status: ConversionStatus }) {
	return (
		<span
			className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_CHIP[status]}`}
			data-testid={`advertiser-portal-status-${status}`}
		>
			{status}
		</span>
	);
}

export function AdvertiserPortalPage() {
	const [advertiserId, setAdvertiserId] = useState<string>(() =>
		readStoredAdvertiserId(),
	);
	const [advertiserDraft, setAdvertiserDraft] = useState<string>(advertiserId);
	const [from, setFrom] = useState<string>(daysAgoIso(30));
	const [to, setTo] = useState<string>(todayIso());
	const [campaignIdInput, setCampaignIdInput] = useState<string>("");
	const [appliedCampaignId, setAppliedCampaignId] = useState<string>("");

	const fromSec = isoToUnixSec(from, false);
	const toSec = isoToUnixSec(to, true);

	const conversionsQuery = useConversionsList({
		advertiserId,
		from: fromSec,
		to: toSec,
		campaignId: appliedCampaignId || undefined,
		limit: 50,
	});
	const dailyQuery = useDailyReport({
		advertiserId,
		from: fromSec,
		to: toSec,
	});
	const campaignQuery = useCampaignReport({
		advertiserId,
		from: fromSec,
		to: toSec,
	});

	const conversions: ConversionView[] =
		conversionsQuery.data?.conversions ?? [];
	const days: DailyRollupRow[] = dailyQuery.data?.days ?? [];
	const campaigns: CampaignAggregateRow[] = campaignQuery.data?.campaigns ?? [];

	const tiles = useMemo(() => {
		return {
			totalConversions: dailyQuery.data?.total_conversions ?? 0,
			totalRevenue: dailyQuery.data?.total_revenue_cents ?? 0,
			totalCampaigns: campaignQuery.data?.total_campaigns ?? 0,
		};
	}, [dailyQuery.data, campaignQuery.data]);

	function handleApplyAdvertiser(e: React.FormEvent) {
		e.preventDefault();
		const value = advertiserDraft.trim();
		setAdvertiserId(value);
		writeStoredAdvertiserId(value);
	}

	function handleApplyCampaign(e: React.FormEvent) {
		e.preventDefault();
		setAppliedCampaignId(campaignIdInput.trim());
	}

	const hasAdvertiser = Boolean(advertiserId);
	const anyError =
		conversionsQuery.error ?? dailyQuery.error ?? campaignQuery.error;

	return (
		<main
			data-testid="advertiser-portal-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6"
		>
			<section
				className="flex flex-col gap-4 rounded-lg border border-zinc-200 bg-white px-5 py-4 shadow-sm"
				aria-label="Advertiser portal controls"
			>
				<div className="flex flex-col gap-1">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						Advertiser portal
					</p>
					<h1 className="text-2xl font-semibold text-zinc-900">
						Conversions & reports
					</h1>
					<p className="text-sm text-zinc-500">
						Advertiser-scoped conversion volume, revenue, and per-campaign
						leaderboard. All requests are bound to the advertiser id below.
					</p>
				</div>

				<form
					onSubmit={handleApplyAdvertiser}
					className="flex flex-wrap items-end gap-3"
					data-testid="advertiser-portal-advertiser-form"
				>
					<div className="flex-1 min-w-[240px]">
						<label
							htmlFor="ap-advertiser"
							className="block text-[11px] font-medium uppercase tracking-wider text-zinc-500"
						>
							Advertiser id (sent as x-advertiser-id)
						</label>
						<input
							id="ap-advertiser"
							type="text"
							value={advertiserDraft}
							onChange={(e) => setAdvertiserDraft(e.currentTarget.value)}
							placeholder="adv_123"
							className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
							data-testid="advertiser-portal-advertiser-input"
						/>
					</div>
					<button
						type="submit"
						className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
						disabled={!advertiserDraft.trim()}
						data-testid="advertiser-portal-advertiser-apply"
					>
						Use advertiser
					</button>
				</form>

				<div className="flex flex-wrap items-end gap-3">
					<div>
						<label
							htmlFor="ap-from"
							className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500"
						>
							<IconCalendar /> From
						</label>
						<input
							id="ap-from"
							type="date"
							value={from}
							onChange={(e) => setFrom(e.currentTarget.value)}
							className="mt-1 w-40 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
							data-testid="advertiser-portal-from"
						/>
					</div>
					<div>
						<label
							htmlFor="ap-to"
							className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500"
						>
							<IconCalendar /> To
						</label>
						<input
							id="ap-to"
							type="date"
							value={to}
							onChange={(e) => setTo(e.currentTarget.value)}
							className="mt-1 w-40 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
							data-testid="advertiser-portal-to"
						/>
					</div>
					<form
						onSubmit={handleApplyCampaign}
						className="flex items-end gap-2"
					>
						<div>
							<label
								htmlFor="ap-campaign"
								className="block text-[11px] font-medium uppercase tracking-wider text-zinc-500"
							>
								Campaign id (optional)
							</label>
							<input
								id="ap-campaign"
								type="text"
								value={campaignIdInput}
								onChange={(e) => setCampaignIdInput(e.currentTarget.value)}
								placeholder="cmp_456"
								className="mt-1 w-56 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
								data-testid="advertiser-portal-campaign-input"
							/>
						</div>
						<button
							type="submit"
							className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
							data-testid="advertiser-portal-campaign-apply"
						>
							Apply
						</button>
					</form>
				</div>
			</section>

			{!hasAdvertiser ? (
				<section
					className="rounded-lg border border-zinc-200 bg-white px-5 py-10 text-center shadow-sm"
					data-testid="advertiser-portal-no-advertiser"
				>
					<p className="text-sm text-zinc-500">
						Enter an advertiser id above to load conversions and reports.
					</p>
				</section>
			) : (
				<>
					<section
						className="grid grid-cols-1 gap-4 sm:grid-cols-3"
						aria-label="Advertiser totals"
					>
						<KpiTile
							label="Conversions"
							value={fmtInt(tiles.totalConversions)}
							icon={<IconTarget />}
							testId="advertiser-portal-tile-conversions"
						/>
						<KpiTile
							label="Revenue"
							value={fmtCents(tiles.totalRevenue)}
							icon={<IconDollar />}
							testId="advertiser-portal-tile-revenue"
						/>
						<KpiTile
							label="Campaigns"
							value={fmtInt(tiles.totalCampaigns)}
							icon={<IconFlag />}
							testId="advertiser-portal-tile-campaigns"
						/>
					</section>

					{anyError ? (
						<p
							className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
							role="alert"
							data-testid="advertiser-portal-error"
						>
							{anyError.message}
						</p>
					) : null}

					<section
						className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm"
						aria-label="Daily rollup"
					>
						<div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
							<h2 className="text-sm font-semibold text-zinc-900">
								Daily rollup (UTC)
							</h2>
							<span className="text-xs text-zinc-500">
								{days.length} {days.length === 1 ? "day" : "days"}
							</span>
						</div>
						{dailyQuery.isLoading ? (
							<p
								className="px-5 py-10 text-sm text-zinc-500"
								data-testid="advertiser-portal-daily-loading"
							>
								Loading.
							</p>
						) : days.length === 0 ? (
							<p
								className="px-5 py-10 text-sm text-zinc-500"
								data-testid="advertiser-portal-daily-empty"
							>
								No activity in the selected range.
							</p>
						) : (
							<div className="overflow-x-auto">
								<table
									className="w-full border-collapse text-sm"
									data-testid="advertiser-portal-daily-table"
								>
									<thead className="bg-zinc-50">
										<tr>
											<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
												Day
											</th>
											<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">
												Conversions
											</th>
											<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">
												Revenue
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-zinc-200">
										{days.map((d) => (
											<tr
												key={d.day}
												data-testid={`advertiser-portal-day-${d.day}`}
												className="transition-colors hover:bg-green-50/40"
											>
												<td className="px-4 py-3 font-mono text-xs text-zinc-700">
													{d.day}
												</td>
												<td className="px-4 py-3 text-right tabular-nums text-zinc-900">
													{fmtInt(d.count)}
												</td>
												<td className="px-4 py-3 text-right tabular-nums text-zinc-900">
													{fmtCents(d.revenue_cents)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</section>

					<section
						className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm"
						aria-label="Campaigns"
					>
						<div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
							<h2 className="text-sm font-semibold text-zinc-900">
								Campaigns leaderboard
							</h2>
							<span className="text-xs text-zinc-500">
								{campaigns.length}{" "}
								{campaigns.length === 1 ? "campaign" : "campaigns"}
							</span>
						</div>
						{campaignQuery.isLoading ? (
							<p
								className="px-5 py-10 text-sm text-zinc-500"
								data-testid="advertiser-portal-campaign-loading"
							>
								Loading.
							</p>
						) : campaigns.length === 0 ? (
							<p
								className="px-5 py-10 text-sm text-zinc-500"
								data-testid="advertiser-portal-campaign-empty"
							>
								No campaigns in the selected range.
							</p>
						) : (
							<div className="overflow-x-auto">
								<table
									className="w-full border-collapse text-sm"
									data-testid="advertiser-portal-campaign-table"
								>
									<thead className="bg-zinc-50">
										<tr>
											<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
												Campaign id
											</th>
											<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">
												Conversions
											</th>
											<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">
												Revenue
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-zinc-200">
										{campaigns.map((c) => (
											<tr
												key={c.campaign_id}
												data-testid={`advertiser-portal-campaign-${c.campaign_id}`}
												className="transition-colors hover:bg-green-50/40"
											>
												<td className="px-4 py-3 font-mono text-xs text-zinc-800">
													{c.campaign_id}
												</td>
												<td className="px-4 py-3 text-right tabular-nums text-zinc-900">
													{fmtInt(c.count)}
												</td>
												<td className="px-4 py-3 text-right tabular-nums text-zinc-900">
													{fmtCents(c.revenue_cents)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</section>

					<section
						className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm"
						aria-label="Recent conversions"
					>
						<div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
							<h2 className="text-sm font-semibold text-zinc-900">
								Recent conversions
							</h2>
							<span className="text-xs text-zinc-500">
								{conversions.length}{" "}
								{conversions.length === 1 ? "row" : "rows"}
								{conversionsQuery.data?.next_cursor ? " (more available)" : ""}
							</span>
						</div>
						{conversionsQuery.isLoading ? (
							<p
								className="px-5 py-10 text-sm text-zinc-500"
								data-testid="advertiser-portal-conversions-loading"
							>
								Loading.
							</p>
						) : conversions.length === 0 ? (
							<p
								className="px-5 py-10 text-sm text-zinc-500"
								data-testid="advertiser-portal-conversions-empty"
							>
								No conversions match the current filters.
							</p>
						) : (
							<div className="overflow-x-auto">
								<table
									className="w-full border-collapse text-sm"
									data-testid="advertiser-portal-conversions-table"
								>
									<thead className="bg-zinc-50">
										<tr>
											<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
												When
											</th>
											<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
												Campaign
											</th>
											<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">
												Amount
											</th>
											<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
												Status
											</th>
											<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
												Id
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-zinc-200">
										{conversions.map((row) => (
											<tr
												key={row.id}
												data-testid={`advertiser-portal-conversion-${row.id}`}
												className="transition-colors hover:bg-green-50/40"
											>
												<td className="px-4 py-3 text-xs text-zinc-700">
													{fmtTs(row.converted_at)}
												</td>
												<td className="px-4 py-3 font-mono text-xs text-zinc-800">
													{row.campaign_id}
												</td>
												<td className="px-4 py-3 text-right tabular-nums text-zinc-900">
													{fmtCents(row.amount_cents)}{" "}
													<span className="text-xs text-zinc-400">
														{row.currency}
													</span>
												</td>
												<td className="px-4 py-3">
													<StatusChip status={row.status} />
												</td>
												<td className="px-4 py-3 font-mono text-xs text-zinc-500">
													{row.id}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</section>
				</>
			)}
		</main>
	);
}

export default AdvertiserPortalPage;
